import { tileToGeometry } from '@viewer3D/components/Map/Tile/helper';
import { useTileTexture } from '@viewer3D/components/Map/Tile/hooks';
import { getRenderOrder } from '@viewer3D/helper';
import { Object3dNames } from '@viewer3D/types';
import { FC, memo, useEffect, useMemo } from 'react';
import { Vector3 } from 'three';
import type { TileProps } from './types';

export const Tile: FC<TileProps> = memo(
  (tile) => {
    const { texture, alkisTexture } = useTileTexture(tile);

    const { tileX, tileY, zoom } = tile;

    // Memoize geometry and position creation
    const { geometry, position } = useMemo(
      () => tileToGeometry(tile),
      [tileX, tileY, zoom, tile.positionZ],
    );

    // Cleanly dispose of the BufferGeometry on unmount
    useEffect(() => {
      return () => {
        geometry.dispose();
      };
    }, [geometry]);

    // Memoize the ALKIS layer position
    const alkisPosition = useMemo(
      () => new Vector3(position.x, position.y, position.z + 0.001),
      [position],
    );

    if (!texture && !alkisTexture) return null;

    return (
      <group>
        {texture && (
          <mesh
            name={`TILE:${tileX}-${tileY}-${zoom}`}
            position={position}
            geometry={geometry}
            renderOrder={getRenderOrder(Object3dNames.Map)}
          >
            <meshBasicMaterial
              map={texture}
              depthTest={false}
              depthWrite={false}
              transparent={false}
              alphaTest={0.1}
            />
          </mesh>
        )}
        {alkisTexture && (
          <mesh
            name={`ALKIS_TILE:${tileX}-${tileY}-${zoom}`}
            position={alkisPosition}
            geometry={geometry}
            renderOrder={getRenderOrder(Object3dNames.AlkisLayer)}
          >
            <meshBasicMaterial
              map={alkisTexture}
              depthTest={false}
              depthWrite={false}
              transparent={false}
              alphaTest={0.5}
            />
          </mesh>
        )}
      </group>
    );
  },
  (prev, next) =>
    prev.tileX === next.tileX &&
    prev.tileY === next.tileY &&
    prev.zoom === next.zoom &&
    prev.style === next.style &&
    prev.positionZ === next.positionZ,
);
